import { ApiResponseModel } from '../types/ApiResponseModel';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { ExitAdjustmentFileListItemModel } from '../types/ExitAdjustmentFileListItemModel';
import { ExitAdjustmentFormViewModel } from '../types/ExitAdjustmentFormViewModel';
import { ExitAdjustmentListItemModel } from '../types/ExitAdjustmentListItemModel';
import { ExitAdjustmentModel } from '../types/ExitAdjustmentModel';
import { SelectOption } from '../types/SelectOption';
import { StudentModel } from '../types/StudentModel';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

const buildListItemUrl = (
  schoolYear: string,
  lea?: string,
  school?: string,
  status?: string
): string => {
  let baseUrl = `exitAdjustment?calendarYear=${schoolYear}`;

  if (lea) {
    baseUrl += `&leaId=${lea}`;
  }
  if (school) {
    baseUrl += `&schoolId=${school}`;
  }
  if (status) {
    baseUrl += `&status=${status}`;
  }

  return baseUrl;
};

export const exitAdjustmentApiPaths = {
  getListItems: (
    schoolYear: string,
    lea?: string,
    school?: string,
    status?: string
  ) => buildListItemUrl(schoolYear, lea, school, status),
  getForm: 'exitAdjustment/form',
  getStudentUpdate: (schoolYear?: string): string => {
    let url = 'exitAdjustment/form/studentUpdate';
    if (schoolYear) {
      url += `?schoolYear=${encodeURIComponent(schoolYear)}`;
    }
    return url;
  },
  getSchoolYears:
    'common/schoolyearsbymodule/formview/ExitAdjustment?showLongYears=true',
  getSchoolsByLeaId: (leaId: string): string =>
    `/common/schoollistforlea/formview/${leaId}`,
  getLeas: '/common/lealistforcurrentuser/formview',
  create: 'exitAdjustment',
  update: 'exitAdjustment',
  getById: (id: string): string => `exitAdjustment/${id}`,
  getStudentByMSISId: (msisId: string, isExitAdjustment: boolean,
    schoolYear?: string): string => {
    let url = `common/student/${msisId}?isExitAdjustment=${isExitAdjustment}`;
    if (schoolYear) {
      url += `&schoolYear=${encodeURIComponent(schoolYear)}`;
    }
    return url;
  },
  delete: (id: string): string => `exitAdjustment/delete/${id}`,
  uploadFile: (
    schoolYear: string,
    studentIdentifier: string,
    leaIdentifier: string,
    associatedRecordId: string
  ): string =>
    `file/exitadjustment?schoolYear=${encodeURIComponent(schoolYear)}` +
    `&studentIdentifier=${encodeURIComponent(studentIdentifier)}` +
    `&leaIdentifier=${encodeURIComponent(leaIdentifier)}` +
    `&associatedRecordId=${encodeURIComponent(associatedRecordId)}`,
  deleteFile: (leaId: string, id: string): string =>
    `file/exitadjustment/${leaId}/${id}`,
  getAllFiles: (leaId: string): string => `file/exitadjustment/${leaId}`,
  downloadFile: (leaId: string, metaId: string): string =>
    `file/exitadjustment/download/${leaId}/${metaId}`,
  getFilesByStudent: (
    schoolYear: string,
    leaId: string,
    msisId: string
  ): string => `file/exitadjustment/${schoolYear}/${leaId}/${msisId}`,
  getFilesByRequest: (leaId: string, associatedRecordId: string): string =>
    `file/exitadjustment/request/${leaId}/${associatedRecordId}`,
};

export const exitAdjustmentServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const getExitAdjustmentListItems = async (
  schoolYear: string,
  lea?: string,
  school?: string,
  status?: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentListItemModel[]>> => {
  return executeApiRequest<ExitAdjustmentListItemModel[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getListItems(schoolYear, lea, school, status)
    );
  });
};

export const getExitAdjustmentFormViewModel = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentFormViewModel>> => {
  return executeApiRequest<ExitAdjustmentFormViewModel>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getForm
    );
  });
};

export const getExitAdjustmentStudentUpdate = async (
  schoolYear?: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<boolean>> => {
  return executeApiRequest<boolean>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getStudentUpdate(schoolYear)
    );
  });
};

export const getExitAdjustmentSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getSchoolYears
    );
  });
};

export const getExitAdjustmentLeas = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getLeas
    );
  });
};

export const getExitAdjustmentSchoolsByLea = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getSchoolsByLeaId(leaId)
    );
  });
};

export const createExitAdjustment = async (
  exitAdjustment: ExitAdjustmentModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    if (!exitAdjustment.reviewStatus || exitAdjustment.reviewStatus === '') {
      exitAdjustment.reviewStatus = 'Pending Approval';
    }

    return await exitAdjustmentServiceClient(config).post(
      exitAdjustmentApiPaths.create,
      exitAdjustment
    );
  });
};

export const updateExitAdjustment = async (
  exitAdjustment: ExitAdjustmentModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    if (!exitAdjustment.reviewStatus || exitAdjustment.reviewStatus === '') {
      exitAdjustment.reviewStatus = 'Pending Approval';
    }

    return await exitAdjustmentServiceClient(config).put(
      exitAdjustmentApiPaths.update,
      exitAdjustment
    );
  });
};

export const getExitAdjustmentById = async (
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentModel>> => {
  return executeApiRequest<ExitAdjustmentModel>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getById(id)
    );
  });
};

export const deleteExitAdjustment = async (
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await exitAdjustmentServiceClient(config).delete(
      exitAdjustmentApiPaths.delete(id)
    );
  });
};

export const getStudentByMSISId = async (
  msisId: string,
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<StudentModel>> => {
  return executeApiRequest<StudentModel>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getStudentByMSISId(msisId, true, schoolYear)
    );
  });
};

export const uploadExitAdjustmentFile = async (
  schoolYear: string,
  studentIdentifier: string,
  leaIdentifier: string,
  associatedRecordId: string,
  file: File,
  config?: DataRequestConfig & {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }
): Promise<ApiResponseModel<{ id: string }>> => {
  const formData = new FormData();
  formData.append('file', file);

  return executeApiRequest<{ id: string }>(async () => {
    return await exitAdjustmentServiceClient(config).upload(
      exitAdjustmentApiPaths.uploadFile(
        schoolYear,
        studentIdentifier,
        leaIdentifier,
        associatedRecordId
      ),
      formData,
      config?.onUploadProgress
    );
  });
};

export const deleteExitAdjustmentFile = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await exitAdjustmentServiceClient(config).delete(
      exitAdjustmentApiPaths.deleteFile(leaId, id)
    );
  });
};

export const getAllExitAdjustmentFiles = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentFileListItemModel[]>> => {
  return executeApiRequest<ExitAdjustmentFileListItemModel[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getAllFiles(leaId)
    );
  });
};

export const downloadExitAdjustmentFile = async (
  leaId: string,
  metaId: string,
  config?: DataRequestConfig
): Promise<Blob> => {
  const response = await exitAdjustmentServiceClient(config).getFile<Blob>(
    exitAdjustmentApiPaths.downloadFile(leaId, metaId)
  );
  return response.data;
};

export const getExitAdjustmentFilesByStudent = async (
  schoolYear: string,
  leaId: string,
  msisId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentFileListItemModel[]>> => {
  return executeApiRequest<ExitAdjustmentFileListItemModel[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getFilesByStudent(schoolYear, leaId, msisId)
    );
  });
};

export const getExitAdjustmentFilesByRequest = async (
  leaId: string,
  associatedRecordId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<ExitAdjustmentFileListItemModel[]>> => {
  return executeApiRequest<ExitAdjustmentFileListItemModel[]>(async () => {
    return await exitAdjustmentServiceClient(config).get(
      exitAdjustmentApiPaths.getFilesByRequest(leaId, associatedRecordId)
    );
  });
};
